import {
    Box,
    Paper,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    useTheme
} from '@mui/material'
import { DashboardEmissionsSpinner } from './GraphLoadingSpinner';
import { emissionsTableWidth } from '../constants/dashboard-constants'

/**
 * Table used to display the calculated emission factors.
 * @param {Object} props 
 * @param {boolean} props.loading Controls the loading state of the table. The style of the loading is baked into this component.
 * @param {{label: string, key: string}[]} props.columns The label value will be displayed in the columns. Key will be used to 'pull out' the associated value from rows.
 * @param {{category: string, value: number, unit: string, percent: string}[]} props.rows Used alongside columns to dynamically place the rows in their respective categories. 
 * - NB: Uses of the words "Total" or "Scope" (with capital letters) will cause special styling for that row.
 * @returns {JSX.Element} A shell styling table that dynamically places the inputted rows and columns.
 */
export default function DashboardEmissionsTable({ loading, columns, rows }) {
    const theme = useTheme();
    const cellBorder = { border: `1px solid ${theme.palette.divider}` }
    const lastIndex = rows?.length - 1
    return (
        <Box sx={{ width: emissionsTableWidth, display: 'flex', flexDirection: 'column' }}>
            {loading
                ? <DashboardEmissionsSpinner />
                : <TableContainer component={Paper} sx={{ borderRadius: 2, ...cellBorder }}>
                    <Table size={'small'} aria-label='a dense table'>
                        <TableHead>
                            <TableRow>
                                {
                                    columns?.length > 0 && columns?.map((column, index) => (
                                        <TableCell
                                            sx={{
                                                fontSize: 20,
                                                fontWeight: 'bold',
                                                py: 2,
                                                ...cellBorder,
                                                borderBottomWidth: '3px'
                                            }}
                                            align='left'
                                            key={index}>{column.label}</TableCell>
                                    ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                rows?.length > 0 && rows?.map((row, rowIndex) => (
                                    <TableRow
                                        key={rowIndex}
                                        sx={{
                                            border: 1,
                                        }}>
                                        {
                                            columns.map((col, colIndex) => (
                                                <TableCell
                                                    key={colIndex}
                                                    align='left'
                                                    sx={{
                                                        ...cellBorder,
                                                        ...(row.category.includes('Total') || row.category.includes('Scope')
                                                            ? { fontWeight: 'bold', fontSize: 20, borderTopWidth: '3px', borderBottomWidth: '3px' }
                                                            : { fontSize: 18 }),
                                                        ...(col.key === 'category' && !row.category.includes('Total') && !row.category.includes('Scope') &&
                                                            { fontStyle: 'italic' }),
                                                        ...(rowIndex === lastIndex && { borderBottomWidth: '1px' })
                                                    }}
                                                >
                                                    {row[col.key]}
                                                </TableCell>
                                            ))
                                        }
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            }
        </Box>
    )
}
